import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setLoading } from './LoadingSlice';
import BaseUrl from '../../BaseUrl';

const API_BASE = BaseUrl.hmsurl;
const FLOORS_URL = `${API_BASE}Floors`;
const ROOMS_URL = `${API_BASE}Rooms`;
const BEDS_URL = `${API_BASE}Beds`;
const ROOM_TYPES_URL = `${API_BASE}RoomTypes`;

// ---------- Floors ----------
export const fetchFloors = createAsyncThunk(
  'manageRooms/fetchFloors',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(FLOORS_URL);
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addFloor = createAsyncThunk(
  'manageRooms/addFloor',
  async (floorName, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.post(FLOORS_URL, {
        floorName: floorName,
        clientId: 'Netsrishti-31ff083e'
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const updateFloor = createAsyncThunk(
  'manageRooms/updateFloor',
  async ({ floorId, floorName }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.put(`${FLOORS_URL}/${floorId}`, {
        floorId: floorId,
        floorName: floorName
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const deleteFloor = createAsyncThunk(
  'manageRooms/deleteFloor',
  async (floorId, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await axios.delete(`${FLOORS_URL}/${floorId}`);
      return floorId;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// ---------- Rooms ----------
export const fetchRoomsByFloor = createAsyncThunk(
  'manageRooms/fetchRoomsByFloor',
  async (floorId, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(ROOMS_URL);
      return {
        floorId,
        rooms: response.data.filter((room) => room.floorId === floorId)
      };
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addRoom = createAsyncThunk(
  'manageRooms/addRoom',
  async ({ floorId, roomName, roomTypeId }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.post(ROOMS_URL, {
        roomName: roomName,
        floorId: floorId,
        roomTypeId: roomTypeId
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const updateRoom = createAsyncThunk(
  'manageRooms/updateRoom',
  async ({ roomId, roomName, roomTypeId }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.put(`${ROOMS_URL}/${roomId}`, {
        roomId: roomId,
        roomName: roomName,
        roomTypeId: roomTypeId
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const deleteRoom = createAsyncThunk(
  'manageRooms/deleteRoom',
  async (roomId, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await axios.delete(`${ROOMS_URL}/${roomId}`);
      return roomId;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// ---------- Beds ----------
export const fetchBedsByRoom = createAsyncThunk(
  'manageRooms/fetchBedsByRoom',
  async (roomId, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(BEDS_URL);
      const roomBeds = response.data.filter((bed) => bed.roomId === roomId);
      return { roomId, beds: roomBeds };
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addBed = createAsyncThunk(
  'manageRooms/addBed',
  async ({ roomId, bedName, cost }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.post(BEDS_URL, {
        roomId: roomId,
        bedName: bedName,
        cost: cost
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const updateBed = createAsyncThunk(
  'manageRooms/updateBed',
  async ({ bedId, bedName, isAvailable, roomId, cost }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.put(`${BEDS_URL}/${bedId}`, {
        bedId: bedId,
        bedName: bedName,
        roomId: roomId,
        isAvailable: isAvailable,
        cost: cost
      });
      return {
        bedId: bedId,
        bedName: bedName,
        roomId: roomId,
        isAvailable: isAvailable,
        cost: cost,
        ...response.data
      };
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const deleteBed = createAsyncThunk(
  'manageRooms/deleteBed',
  async (bedId, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      await axios.delete(`${BEDS_URL}/${bedId}`);
      return bedId;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// ---------- Room Types ----------
export const fetchRoomTypes = createAsyncThunk(
  'manageRooms/fetchRoomTypes',
  async (_, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.get(ROOM_TYPES_URL);
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const addRoomType = createAsyncThunk(
  'manageRooms/addRoomType',
  async ({ roomTypeId, roomTypeName }, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const response = await axios.post(ROOM_TYPES_URL, {
        roomTypeId: roomTypeId,
        roomTypeName: roomTypeName
      });
      return response.data;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

// ---------- Initial State ----------
const initialState = {
  floors: [],
  roomsData: {},
  bedsData: {},
  selectedFloorId: null,
  selectedRoomId: null,
  status: 'idle',
  error: null,
  roomTypes: [],
  roomTypesStatus: 'idle',
  roomTypesError: null
};

const manageRoomsSlice = createSlice({
  name: 'manageRooms',
  initialState,
  reducers: {
    setSelectedFloor: (state, action) => {
      state.selectedFloorId = action.payload;
      state.selectedRoomId = null;
    },
    setSelectedRoom: (state, action) => {
      state.selectedRoomId = action.payload;
    },
    refreshBeds: (state, action) => {
      const roomId = action.payload;
      if (state.bedsData[roomId]) {
        state.bedsData[roomId] = [...state.bedsData[roomId]];
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Floors
      .addCase(fetchFloors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchFloors.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.floors = action.payload.map((floor) => ({
          id: floor.floorId,
          floorId: floor.floorId,
          name: floor.floorName,
          floorName: floor.floorName
        }));
      })
      .addCase(fetchFloors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(addFloor.fulfilled, (state, action) => {
        const newFloor = {
          id: action.payload.floorId,
          floorId: action.payload.floorId,
          name: action.payload.floorName,
          floorName: action.payload.floorName
        };
        state.floors.push(newFloor);
      })
      .addCase(updateFloor.fulfilled, (state, action) => {
        const updatedData = action.payload;
        const updatedFloor = {
          id: updatedData.floorId,
          floorId: updatedData.floorId,
          name: updatedData.floorName,
          floorName: updatedData.floorName
        };
        const index = state.floors.findIndex((floor) => floor.id === updatedFloor.id);
        if (index !== -1) {
          state.floors[index] = updatedFloor;
        }
      })
      .addCase(deleteFloor.fulfilled, (state, action) => {
        state.floors = state.floors.filter((floor) => floor.id !== action.payload);
        if (state.selectedFloorId === action.payload) {
          state.selectedFloorId = null;
          state.selectedRoomId = null;
        }
      })

      // Rooms
      .addCase(fetchRoomsByFloor.fulfilled, (state, action) => {
        const { floorId, rooms } = action.payload;
        state.roomsData[floorId] = rooms.map((room) => ({
          id: room.roomId,
          roomId: room.roomId,
          name: room.roomName,
          roomName: room.roomName,
          roomTypeId: room.roomTypeId,
          roomTypeName: room.roomTypeName,
          floorId: room.floorId
        }));
      })
      .addCase(addRoom.fulfilled, (state, action) => {
        const newRoom = {
          id: action.payload.roomId,
          roomId: action.payload.roomId,
          name: action.payload.roomName,
          roomName: action.payload.roomName,
          roomTypeId: action.payload.roomTypeId,
          roomTypeName: action.payload.roomTypeName,
          floorId: action.payload.floorId
        };
        const floorId = action.payload.floorId;
        if (!state.roomsData[floorId]) {
          state.roomsData[floorId] = [];
        }
        state.roomsData[floorId].push(newRoom);
      })
      .addCase(updateRoom.fulfilled, (state, action) => {
        const updatedData = action.payload;
        const updatedRoom = {
          id: updatedData.roomId,
          roomId: updatedData.roomId,
          name: updatedData.roomName,
          roomName: updatedData.roomName,
          roomTypeId: updatedData.roomTypeId,
          roomTypeName: updatedData.roomTypeName,
          floorId: updatedData.floorId
        };
        const floorId = updatedData.floorId;
        if (state.roomsData[floorId]) {
          const index = state.roomsData[floorId].findIndex(
            (room) => room.id === updatedRoom.id
          );
          if (index !== -1) {
            state.roomsData[floorId][index] = updatedRoom;
          }
        } else {
          Object.keys(state.roomsData).forEach((key) => {
            const idx = state.roomsData[key].findIndex(
              (room) => room.id === updatedRoom.id
            );
            if (idx !== -1) {
              state.roomsData[key][idx] = updatedRoom;
            }
          });
        }
      })
      .addCase(deleteRoom.fulfilled, (state, action) => {
        const roomId = action.payload;
        Object.keys(state.roomsData).forEach((floorId) => {
          state.roomsData[floorId] = state.roomsData[floorId].filter(
            (room) => room.id !== roomId
          );
        });
        delete state.bedsData[roomId];
        if (state.selectedRoomId === roomId) {
          state.selectedRoomId = null;
        }
      })

      // Beds
      .addCase(fetchBedsByRoom.fulfilled, (state, action) => {
        const { roomId, beds } = action.payload;
        state.bedsData[roomId] = beds.map((bed) => ({
          id: bed.bedId,
          bedId: bed.bedId,
          name: bed.bedName || `Bed ${bed.bedId}`,
          bedName: bed.bedName || `Bed ${bed.bedId}`,
          isAvailable: bed.isAvailable,
          roomId: bed.roomId,
          cost: bed.cost !== undefined ? bed.cost : 0,
          rm: bed.rm,
          ...bed
        }));
      })
      .addCase(addBed.fulfilled, (state, action) => {
        const newBed = {
          id: action.payload.bedId,
          bedId: action.payload.bedId,
          name: action.payload.bedName || `Bed ${action.payload.bedId}`,
          bedName: action.payload.bedName || `Bed ${action.payload.bedId}`,
          isAvailable:
            action.payload.isAvailable !== undefined ? action.payload.isAvailable : true,
          roomId: action.payload.roomId,
          cost: action.payload.cost !== undefined ? action.payload.cost : 0
        };
        const roomId = action.payload.roomId;
        if (!state.bedsData[roomId]) {
          state.bedsData[roomId] = [];
        }
        state.bedsData[roomId].push(newBed);
      })
      .addCase(updateBed.fulfilled, (state, action) => {
        const updatedData = action.payload;
        const updatedBed = {
          id: updatedData.bedId,
          bedId: updatedData.bedId,
          name: updatedData.bedName || `Bed ${updatedData.bedId}`,
          bedName: updatedData.bedName || `Bed ${updatedData.bedId}`,
          isAvailable: updatedData.isAvailable,
          roomId: updatedData.roomId,
          cost: updatedData.cost !== undefined ? updatedData.cost : 0
        };
        const roomId = updatedData.roomId;
        let bedFound = false;
        if (state.bedsData[roomId]) {
          const index = state.bedsData[roomId].findIndex(
            (bed) => bed.id === updatedBed.id || bed.bedId === updatedBed.id
          );
          if (index !== -1) {
            const newBedsArray = [...state.bedsData[roomId]];
            newBedsArray[index] = { ...newBedsArray[index], ...updatedBed };
            state.bedsData[roomId] = newBedsArray;
            bedFound = true;
          }
        }
        if (!bedFound) {
          Object.keys(state.bedsData).forEach((key) => {
            const idx = state.bedsData[key].findIndex(
              (bed) => bed.id === updatedBed.id || bed.bedId === updatedBed.id
            );
            if (idx !== -1) {
              const newBedsArray = [...state.bedsData[key]];
              newBedsArray[idx] = { ...newBedsArray[idx], ...updatedBed };
              state.bedsData[key] = newBedsArray;
              bedFound = true;
            }
          });
        }
      })
      .addCase(deleteBed.fulfilled, (state, action) => {
        const bedId = action.payload;
        Object.keys(state.bedsData).forEach((roomId) => {
          state.bedsData[roomId] = state.bedsData[roomId].filter(
            (bed) => bed.id !== bedId
          );
        });
      })

      // Room Types
      .addCase(fetchRoomTypes.pending, (state) => {
        state.roomTypesStatus = 'loading';
      })
      .addCase(fetchRoomTypes.fulfilled, (state, action) => {
        state.roomTypesStatus = 'succeeded';
        state.roomTypes = action.payload.map((rt) => ({
          id: rt.roomTypeId,
          roomTypeId: rt.roomTypeId,
          name: rt.roomTypeName,
          roomTypeName: rt.roomTypeName
        }));
      })
      .addCase(fetchRoomTypes.rejected, (state, action) => {
        state.roomTypesStatus = 'failed';
        state.roomTypesError = action.error.message;
      })
      .addCase(addRoomType.fulfilled, (state, action) => {
        const newType = {
          id: action.payload.roomTypeId,
          roomTypeId: action.payload.roomTypeId,
          name: action.payload.roomTypeName,
          roomTypeName: action.payload.roomTypeName
        };
        state.roomTypes.push(newType);
      });
  }
});

export const { setSelectedFloor, setSelectedRoom, refreshBeds } =
  manageRoomsSlice.actions;

export default manageRoomsSlice.reducer;